import { useCallback, useEffect, useMemo, useRef, useState, type UIEvent } from 'react'

import {
  type AgentMode,
  type AgentPendingAction,
  type AgentRunResponse,
  type AgentSkill,
  type AgentToolDescriptor,
  listAgentSkills,
  listAgentTools,
  streamAgentConfirm,
  streamAgentRun,
} from '../api/agents'
import type { ClientUiTextModel } from '../api/platformConfig'
import type { AgentActivityState } from '../components/agent/AgentActivityBar'
import { AgentComposerDock } from '../components/agent/AgentComposerDock'
import { AgentInlinePicker } from '../components/agent/AgentInlinePicker'
import { AgentQuestionOverlay, type AgentQuestion } from '../components/agent/AgentQuestionOverlay'
import type { AgentGenerationCardData } from '../components/agent/AgentGenerationCard'
import { agentToolLabel } from '../components/agent/agentDisplay'
import {
  AgentRunCard,
  type AgentTimelineBlock,
} from '../components/agent/AgentRunCard'
import { ChromeToolbarButton, ChromeToolbarDangerButton } from '../components/ChromeActions'
import { ChromeMetaTag } from '../components/ChromeTags'
import { IconTrash } from '../components/nav-icons'
import { ChromeTooltip } from '../components/ChromeTooltip'
import { ThemeModal } from '../components/ThemeModal'
import { usePlatformConfig } from '../context/PlatformConfigContext'
import { useToast } from '../context/ToastContext'
import { USER_PERMISSION, useUserPerm } from '../hooks/useUserPerm'
import { ContentSkeleton } from '../layouts/ContentSkeleton'
import { applyAgentStreamEvent } from '../lib/agentTimelineMapper'
import { showApiErrorToast } from '../lib/apiErrorToast'
import { platformDisplayLabel } from '../lib/platformDisplayLabel'
import { useTheme } from '../theme/ThemeContext'

function formatAgentModelValue(item: Pick<ClientUiTextModel, 'platform' | 'id'>): string {
  return item.platform ? `${item.platform}/${item.id}` : item.id
}

function isAvailableAgentModel(modelRef: string, textModels: ClientUiTextModel[]): boolean {
  const trimmed = modelRef.trim()
  if (!trimmed) return false
  return textModels.some((item) => formatAgentModelValue(item) === trimmed)
}

/**
 * 只认真实模型：选中项仍可用就用它，失效时回落到第一条可用模型。
 * 没有「自动选择 / 系统默认」这种虚拟项；一个可用模型都没有时返回空串，由调用方禁用智能体。
 */
function resolveAgentModelValue(selected: string, textModels: ClientUiTextModel[]): string {
  const trimmed = selected.trim()
  if (trimmed && isAvailableAgentModel(trimmed, textModels)) return trimmed
  const first = textModels[0]
  return first ? formatAgentModelValue(first) : ''
}

const MODE_OPTIONS = [
  { value: 'auto', label: '自动模式 (Auto)', description: '由智能体自行决定执行方式' },
  { value: 'react', label: '即时执行 (ReAct)', description: '边想边做，适合短链路任务' },
  { value: 'plan_execute', label: '计划执行 (Plan-Execute)', description: '先规划步骤，再逐步完成' },
] as const

type ExplicitAgentMode = (typeof MODE_OPTIONS)[number]['value']

/** 开发预览：点击发送后模拟完整推理循环；正式环境走真实 SSE */
const PREVIEW_SIMULATE_LOOP = false

type AgentChatMessage = {
  id: string
  role: 'assistant' | 'user' | 'system'
  text?: string
  status?: string
  meta?: string
  timeline?: AgentTimelineBlock[]
  activity?: AgentActivityState | null
  showConfirm?: boolean
}

/** 页面会话缓存（sessionStorage）：离开页面再回来恢复对话与输入状态，关闭浏览器即清除 */
const PAGE_CACHE_KEY = 'glyphic-agents-page-cache-v1'

type AgentsPageCache = {
  messages?: AgentChatMessage[]
  goal?: string
  mode?: string
  model?: string
  selectedSkillIds?: string[]
  activeRunPublicId?: string | null
  composerCollapsed?: boolean
  /** 用户手动展开/收起的 timeline process/todo/subagent block id 集合 */
  openBlockIds?: string[]
}

/**
 * 读取页面会话缓存。运行中的消息恢复时显式标记为「已中断」：
 * SSE 运行流随页面卸载断开，前端无法重连，保留假 running 状态会误导用户。
 */
function readPageCache(): AgentsPageCache {
  try {
    const raw = sessionStorage.getItem(PAGE_CACHE_KEY)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return {}
    const cache = parsed as AgentsPageCache
    if (Array.isArray(cache.messages)) {
      cache.messages = cache.messages.map((item) => {
        if (
          item?.status !== 'running' &&
          item.status !== 'pending' &&
          item.status !== 'awaiting_confirmation'
        ) {
          return item
        }
        return {
          ...item,
          status: 'failed',
          showConfirm: false,
          activity: { label: '已中断：页面离开后运行流断开，请重新发起', kind: 'error' as const },
          timeline: [
            ...(item.timeline ?? []),
            { id: `interrupted-${item.id}`, type: 'answer' as const, text: '本次运行在页面离开后中断，未完成的任务请重新发起。' },
          ],
        }
      })
    }
    return cache
  } catch {
    return {}
  }
}

function humanizePendingSummary(action: AgentPendingAction): string {
  const thought = action.assistantThought?.trim()
  if (thought) return thought
  if (action.toolName === 'ask_user') return '提问中。'
  return `准备执行「${agentToolLabel(action.toolName)}」。确认后才会真正创建任务。`
}

function cloneTimeline(blocks: AgentTimelineBlock[]): AgentTimelineBlock[] {
  return blocks.map((block) => {
    if (block.type === 'answer') return { ...block }
    if (block.type === 'process') {
      return {
        ...block,
        lines: block.lines.map((line) => ({ ...line })),
      }
    }
    if (block.type === 'todos') {
      return {
        ...block,
        items: block.items.map((item) => ({ ...item })),
      }
    }
    if (block.type === 'subagent') {
      return {
        ...block,
        lines: block.lines?.map((line) => ({ ...line })),
        activity: block.activity ? { ...block.activity } : block.activity,
      }
    }
    if (block.type === 'images') {
      return { ...block, images: block.images.map((image) => ({ ...image })) }
    }
    return { ...block }
  })
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export function AgentsPage() {
  const { theme } = useTheme()
  const angular = theme === 'pixel' || theme === 'geek'
  const { showToast } = useToast()
  const { clientUi } = usePlatformConfig()
  const canRun = useUserPerm(USER_PERMISSION.agentRunCreate)
  const canConfirm = useUserPerm(USER_PERMISSION.agentRunConfirm)

  // 页面会话缓存：一次性读取，关闭浏览器即清除（sessionStorage）
  const [restoredCache] = useState<AgentsPageCache>(readPageCache)

  const [goal, setGoal] = useState(() => restoredCache.goal ?? '')
  const [mode, setMode] = useState<ExplicitAgentMode>(() =>
    restoredCache.mode === 'react' || restoredCache.mode === 'plan_execute' ? restoredCache.mode : 'auto',
  )
  const [model, setModel] = useState(() => restoredCache.model ?? '')
  const [composerCollapsed, setComposerCollapsed] = useState(restoredCache.composerCollapsed ?? false)
  /** 用户手动展开的 timeline block id（process/todo/subagent）——页面返回后恢复展开态 */
  const [openBlockIds, setOpenBlockIds] = useState<Set<string>>(
    () => new Set(Array.isArray(restoredCache.openBlockIds) ? restoredCache.openBlockIds : []),
  )

  const [tools, setTools] = useState<AgentToolDescriptor[]>([])
  const [toolsLoading, setToolsLoading] = useState(true)
  const [toolsAvailable, setToolsAvailable] = useState(false)
  // 本次运行禁用的工具名集合（勾掉 = 不下发给模型，从源头不可调用）
  const [disabledToolNames, setDisabledToolNames] = useState<string[]>([])
  // 技能引用：管理端配置、用户端斜杠引用；原工具/模型/模式选择保留为默认引用
  const [skills, setSkills] = useState<AgentSkill[]>([])
  const [selectedSkillIds, setSelectedSkillIds] = useState<string[]>(() =>
    Array.isArray(restoredCache.selectedSkillIds)
      ? restoredCache.selectedSkillIds.filter((id) => typeof id === 'string')
      : [],
  )
  const [running, setRunning] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [pendingAction, setPendingAction] = useState<AgentPendingAction | null>(null)
  // ask_user 交互工具：pendingAction.input.questions 解析为覆盖层问题列表；null 表示非提问场景
  const askUserQuestions = useMemo<AgentQuestion[] | null>(() => {
    if (!pendingAction || pendingAction.toolName !== 'ask_user') return null
    const raw = pendingAction.input?.questions
    if (!Array.isArray(raw) || raw.length === 0) return null
    const questions: AgentQuestion[] = []
    for (const item of raw) {
      if (!item || typeof item !== 'object') continue
      const record = item as Record<string, unknown>
      const question = typeof record.question === 'string' ? record.question.trim() : ''
      if (!question) continue
      const options: AgentQuestion['options'] = []
      if (Array.isArray(record.options)) {
        for (const option of record.options) {
          if (!option || typeof option !== 'object') continue
          const opt = option as Record<string, unknown>
          const value = typeof opt.value === 'string' ? opt.value : ''
          const label = typeof opt.label === 'string' ? opt.label : value
          if (!value && !label) continue
          options.push({
            value,
            label,
            description: typeof opt.description === 'string' && opt.description.trim() ? opt.description.trim() : undefined,
          })
        }
      }
      questions.push({
        question,
        options,
        multiSelect: record.multiSelect === true,
      })
    }
    return questions.length > 0 ? questions : null
  }, [pendingAction])
  const [messages, setMessages] = useState<AgentChatMessage[]>(() => restoredCache.messages ?? [])
  const messageViewportRef = useRef<HTMLDivElement | null>(null)
  const [activeRunId, setActiveRunId] = useState<string | null>(null)
  const [activeRunPublicId, setActiveRunPublicId] = useState<string | null>(
    () => restoredCache.activeRunPublicId ?? null,
  )
  const latestTimelineRef = useRef<AgentTimelineBlock[]>([])
  const answerScrollKeyRef = useRef('')
  const streamAbortRef = useRef<(() => void) | null>(null)
  // 模型静默思考指示：流式事件停止超过 THINKING_IDLE_MS 时在时间线底部显示占位，
  // 新内容到达即被覆盖。上游 LLM 偶发长静默（长工具执行 / 网关断流重试）时给用户反馈。
  const [streamIdle, setStreamIdle] = useState(false)
  const streamIdleTimerRef = useRef<number | null>(null)
  const bumpStreamActivity = useCallback(() => {
    setStreamIdle(false)
    if (streamIdleTimerRef.current != null) window.clearTimeout(streamIdleTimerRef.current)
    streamIdleTimerRef.current = window.setTimeout(() => setStreamIdle(true), 3000)
  }, [])
  const clearStreamIdle = useCallback(() => {
    if (streamIdleTimerRef.current != null) {
      window.clearTimeout(streamIdleTimerRef.current)
      streamIdleTimerRef.current = null
    }
    setStreamIdle(false)
  }, [])
  // 吸底跟随：用户主动上滚离开底部后，流式更新不再强制拉回底部
  const stickToBottomRef = useRef(true)

  const handleViewportScroll = useCallback((event: UIEvent<HTMLDivElement>) => {
    const el = event.currentTarget
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120
  }, [])

  const pushMessage = useCallback((message: AgentChatMessage) => {
    setMessages((prev) => [...prev, message])
  }, [])

  // 页面会话缓存：对话与输入状态防抖写入 sessionStorage（关闭浏览器即清除），离开页面回来全部恢复
  useEffect(() => {
    const timer = window.setTimeout(() => {
      try {
        const cache: AgentsPageCache = {
          messages,
          goal,
          mode,
          model,
          selectedSkillIds,
          activeRunPublicId,
          composerCollapsed,
          openBlockIds: Array.from(openBlockIds),
        }
        sessionStorage.setItem(PAGE_CACHE_KEY, JSON.stringify(cache))
      } catch {
        // 缓存只是体验增强：序列化超限或存储不可用时跳过
      }
    }, 400)
    return () => window.clearTimeout(timer)
  }, [messages, goal, mode, model, selectedSkillIds, activeRunPublicId, composerCollapsed, openBlockIds])

  const patchMessage = useCallback((id: string, patch: Partial<AgentChatMessage>) => {
    setMessages((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)))
  }, [])

  /** 用户手动展开/收起时间线折叠块——持久化到 sessionStorage，离开页面后恢复 */
  const handleBlockToggle = useCallback((blockId: string, open: boolean) => {
    setOpenBlockIds((prev) => {
      const next = new Set(prev)
      if (open) next.add(blockId)
      else next.delete(blockId)
      return next
    })
  }, [])

  /** 生图任务卡片状态同步：SSE 终态 / 重试换任务号时由卡片回调，就地更新对应时间线块 */
  const patchGenerationCard = useCallback(
    (messageId: string, blockId: string, patch: Partial<AgentGenerationCardData>) => {
      setMessages((prev) =>
        prev.map((item) => {
          if (item.id !== messageId || !item.timeline) return item
          return {
            ...item,
            timeline: item.timeline.map((block) =>
              block.id === blockId && block.type === 'generation-card'
                ? { ...block, data: { ...block.data, ...patch } }
                : block,
            ),
          }
        }),
      )
    },
    [],
  )

  useEffect(() => {
    const active = messages.find((item) => item.id === activeRunId)
    const answers = (active?.timeline ?? [])
      .filter((block): block is Extract<AgentTimelineBlock, { type: 'answer' }> => block.type === 'answer')
      .map((block) => block.text)
      .join('\n')
    if (!activeRunId || !answers || answers === answerScrollKeyRef.current) return
    answerScrollKeyRef.current = answers
    // 用户已上滚查看历史时不跟随，避免被强制拉回底部
    if (!stickToBottomRef.current) return
    const node = messageViewportRef.current
    if (!node) return
    node.scrollTo({ top: node.scrollHeight, behavior: 'smooth' })
  }, [messages, activeRunId])

  /**
   * 清空当前对话：终止进行中的运行流，重置全部会话状态；页面缓存随之写入空列表。
   * 入口的图标按钮只负责打开 ThemeModal 二次确认，真正清空在这里执行。
   */
  const clearConversation = useCallback(() => {
    streamAbortRef.current?.()
    streamAbortRef.current = null
    clearStreamIdle()
    latestTimelineRef.current = []
    setMessages([])
    setActiveRunId(null)
    setActiveRunPublicId(null)
    setPendingAction(null)
    setConfirming(false)
    setRunning(false)
    showToast({ message: '对话已清空', variant: 'success' })
  }, [clearStreamIdle, showToast])

  const [confirmClearOpen, setConfirmClearOpen] = useState(false)

  const refreshTools = useCallback(async (silent = false) => {
    setToolsLoading(true)
    try {
      const items = await listAgentTools()
      setTools(items)
      setToolsAvailable(true)
    } catch (error) {
      setTools([])
      setToolsAvailable(false)
      if (!silent) {
        showApiErrorToast(showToast, error, '加载智能体工具失败', 'error')
      }
    } finally {
      setToolsLoading(false)
    }
  }, [showToast])

  useEffect(() => {
    void refreshTools(true)
  }, [refreshTools])

  // 技能目录静默加载：失败不影响智能体可用性，仅斜杠引用不可用
  useEffect(() => {
    let cancelled = false
    listAgentSkills()
      .then((items) => {
        if (!cancelled) setSkills(items)
      })
      .catch(() => {
        if (!cancelled) setSkills([])
      })
    return () => {
      cancelled = true
    }
  }, [])

  const skillOptions = useMemo(
    () =>
      skills.map((item) => ({
        value: item.publicId,
        label: item.name,
        description: item.description,
        meta: '技能',
      })),
    [skills],
  )

  const selectedSkills = useMemo(
    () => skillOptions.filter((option) => selectedSkillIds.includes(option.value)),
    [skillOptions, selectedSkillIds],
  )

  const toggleSkill = useCallback((publicId: string) => {
    setSelectedSkillIds((prev) =>
      prev.includes(publicId) ? prev.filter((item) => item !== publicId) : [...prev, publicId],
    )
  }, [])

  useEffect(() => {
    return () => {
      streamAbortRef.current?.()
    }
  }, [])

  const toolCountLabel = toolsLoading
    ? '工具连接中'
    : toolsAvailable
      ? `已接入 ${tools.length} 个工具`
      : '工具不可用'

  const toolOptions = useMemo(
    () =>
      tools.map((item) => ({
        value: item.name,
        // 显示中文标签（与时间线/确认弹层一致），value 保持内部名用于禁用开关
        label: agentToolLabel(item.name),
        description: item.description,
        meta: item.group || undefined,
      })),
    [tools],
  )

  const toggleTool = useCallback((name: string) => {
    setDisabledToolNames((prev) =>
      prev.includes(name) ? prev.filter((item) => item !== name) : [...prev, name],
    )
  }, [])

  const toolsTriggerLabel = toolsLoading
    ? '工具…'
    : `工具 ${tools.length - disabledToolNames.length}/${tools.length}`

  /**
   * 可用性只看「有没有可用的文本模型」：模型目录已按「模型启用 + 平台启用 + 平台有密钥」过滤。
   * 有模型就直接出现在下拉里，一个都没有就禁用智能体。
   */
  const agentReady = useMemo(() => clientUi.textModels.length > 0, [clientUi.textModels])

  const agentUnavailableReason = useMemo(() => {
    if (clientUi.textModels.length === 0) {
      return '智能体暂不可用：当前没有可用的对话模型，请联系管理人员。'
    }
    return ''
  }, [clientUi.textModels])

  const introText = !agentReady
    ? agentUnavailableReason
    : toolsLoading
      ? '正在连接工具目录，你也可以先描述目标。'
      : toolsAvailable
        ? ''
        : '当前工具目录不可用，仍可发送目标做对话与规划；需要工具执行时请稍后刷新工具。'

  // 无可用文本模型时智能体直接禁用
  const canSubmit = canRun && agentReady

  useEffect(() => {
    setMessages((prev) => {
      // 正常可用状态下不注入任何预制开场白；仅在异常/加载中给出必要提示
      if (!introText.trim()) {
        return prev.filter((item) => item.id !== 'agent-intro')
      }
      const intro = prev.find((item) => item.id === 'agent-intro')
      const nextIntro: AgentChatMessage = {
        id: 'agent-intro',
        role: 'system',
        text: introText,
      }
      if (!intro) return [nextIntro, ...prev]
      if (intro.text === nextIntro.text) return prev
      return prev.map((item) => (item.id === 'agent-intro' ? nextIntro : item))
    })
  }, [introText])

  const modeOptions = useMemo(
    () =>
      MODE_OPTIONS.map((item) => ({
        value: item.value,
        label: item.label,
        description: item.description,
      })),
    [],
  )

  const modelOptions = useMemo(() => {
    if (!agentReady) {
      return [
        {
          value: '',
          label: '暂无可用模型',
          description: agentUnavailableReason || '智能体暂不可用，请联系管理人员。',
          meta: 'Unavailable',
        },
      ]
    }
    // 只列真实可用的模型，不插入「自动选择 / 系统默认」这类虚拟项
    return clientUi.textModels.map((item) => ({
      value: formatAgentModelValue(item),
      label: item.label || item.id,
      description: item.description || item.id,
      meta: platformDisplayLabel(item.platform),
    }))
  }, [agentReady, agentUnavailableReason, clientUi.textModels])

  /** 下拉默认落在第一条可用模型上（显示的就是真实模型名，不是「自动」） */
  useEffect(() => {
    if (!agentReady) return
    setModel((prev) => {
      const resolved = resolveAgentModelValue(prev, clientUi.textModels)
      return resolved && resolved !== prev ? resolved : prev
    })
  }, [agentReady, clientUi.textModels])

  function sleep(ms: number) {
    return new Promise<void>((resolve) => {
      window.setTimeout(resolve, ms)
    })
  }

  async function streamAnswer(
    runId: string,
    answerId: string,
    fullText: string,
    timeline: AgentTimelineBlock[],
    status: string,
    meta: string,
    showConfirm = false,
  ) {
    const chunkSize = 2
    let built = ''
    for (let i = 0; i < fullText.length; i += chunkSize) {
      built += fullText.slice(i, i + chunkSize)
      const next = cloneTimeline(timeline)
      const target = next.find((item) => item.id === answerId && item.type === 'answer')
      if (target && target.type === 'answer') target.text = built
      latestTimelineRef.current = next
      patchMessage(runId, { status, meta, timeline: next, showConfirm })
      await sleep(22)
    }
    const local = timeline.find((item) => item.id === answerId && item.type === 'answer')
    if (local && local.type === 'answer') local.text = fullText
  }

  async function playSimulatedLoop(
    runId: string,
    submittedGoal: string,
    submittedMode: AgentMode,
    modelLabel: string,
    modeLabel: string,
  ) {
    const selectedMode = submittedMode === 'auto' ? 'plan_execute' : submittedMode
    const selectedModeLabel =
      MODE_OPTIONS.find((item) => item.value === selectedMode)?.label ?? selectedMode
    const headerMeta = `${modelLabel} · ${modeLabel}`

    const timeline: AgentTimelineBlock[] = []
    const publish = (status: string, showConfirm = false) => {
      const snap = cloneTimeline(timeline)
      latestTimelineRef.current = snap
      patchMessage(runId, { status, meta: headerMeta, timeline: snap, showConfirm })
    }

    const pushProcess = (block: Extract<AgentTimelineBlock, { type: 'process' }>) => {
      timeline.push({ ...block, collapsedByDefault: block.collapsedByDefault ?? true })
    }

    const pushAnswer = (id: string, text = '') => {
      timeline.push({ id, type: 'answer', text })
    }

    const setTodoStatus = (
      id: string,
      status: 'pending' | 'in_progress' | 'completed' | 'cancelled',
    ) => {
      const block = timeline.find((item) => item.id === 'todos' && item.type === 'todos')
      if (!block || block.type !== 'todos') return
      block.items = block.items.map((item) => (item.id === id ? { ...item, status } : item))
      const allDone = block.items.every((item) => item.status === 'completed')
      if (allDone) {
        block.forceOpen = false
        block.collapsedByDefault = true
      }
    }

    // —— 阶段 1：规划 To-do（Cursor 式待办列表）——
    pushProcess({
      id: 'proc-mode',
      type: 'process',
      summary:
        submittedMode === 'auto'
          ? `已自动选择「${selectedModeLabel}」`
          : `使用「${selectedModeLabel}」`,
      status: 'completed',
      collapsedByDefault: true,
      lines: [
        {
          id: 'mode',
          kind: 'mode',
          label:
            submittedMode === 'auto'
              ? `围绕目标拆解多轮检索与写入确认`
              : `按你的选择执行`,
          status: 'completed',
          detail:
            submittedMode === 'auto'
              ? `目标「${submittedGoal.slice(0, 40)}${submittedGoal.length > 40 ? '…' : ''}」`
              : undefined,
        },
      ],
    })
    timeline.push({
      id: 'todos',
      type: 'todos',
      forceOpen: true,
      collapsedByDefault: false,
      items: [
        { id: 't1', content: '读取项目与当前页上下文', status: 'pending' },
        { id: 't2', content: '核对相邻页连续性', status: 'pending' },
        { id: 't3', content: '并行检索夜景构图参考', status: 'pending' },
        { id: 't4', content: '并行检索逆光/霓虹样本', status: 'pending' },
        { id: 't5', content: '对比候选并收敛方案', status: 'pending' },
        { id: 't6', content: '组织重绘提示词', status: 'pending' },
        { id: 't7', content: '确认后创建页级生图', status: 'pending' },
      ],
    })
    setTodoStatus('t1', 'in_progress')
    publish('running')
    await sleep(520)

    pushAnswer('ans-1')
    await streamAnswer(
      runId,
      'ans-1',
      '先对齐项目与相邻页，再并行检索构图与光影参考。',
      timeline,
      'running',
      headerMeta,
    )
    await sleep(360)

    // —— 阶段 2：并行读取（同批多工具）——
    setTodoStatus('t1', 'completed')
    setTodoStatus('t2', 'in_progress')
    pushProcess({
      id: 'proc-context',
      type: 'process',
      summary: '并行读取项目与相邻页 · 2 个工具',
      status: 'running',
      collapsedByDefault: true,
      lines: [
        {
          id: 't-project',
          kind: 'tool',
          label: agentToolLabel('comic.project.read'),
          status: 'running',
          meta: '并行',
          detail: '第 1 话第 3 页设定与镜头备注',
        },
        {
          id: 't-neighbors',
          kind: 'tool',
          label: `${agentToolLabel('comic.project.read')} · 相邻页`,
          status: 'running',
          meta: '并行',
          detail: '第 2 页收束 / 第 4 页开场',
        },
      ],
    })
    publish('running')
    await sleep(700)

    const procContext = timeline.find((b) => b.id === 'proc-context' && b.type === 'process')
    if (procContext && procContext.type === 'process') {
      procContext.status = 'completed'
      procContext.summary = '已读取项目与相邻页 · 2 个工具'
      procContext.lines = [
        {
          id: 't-project',
          kind: 'tool',
          label: agentToolLabel('comic.project.read'),
          status: 'completed',
          meta: '并行',
        },
        {
          id: 't-neighbors',
          kind: 'tool',
          label: `${agentToolLabel('comic.project.read')} · 相邻页`,
          status: 'completed',
          meta: '并行',
        },
        {
          id: 'o-context',
          kind: 'observation',
          label: '第 3 页偏平中景，应承接第 2→4 页压迫感推进',
          status: 'completed',
        },
        {
          id: 'th-1',
          kind: 'thought',
          label: '宽检索构图 + 逆光霓虹补光影；不足再收窄巷口透视',
          status: 'completed',
        },
      ]
    }
    setTodoStatus('t2', 'completed')
    setTodoStatus('t3', 'in_progress')
    setTodoStatus('t4', 'in_progress')
    publish('running')
    await sleep(280)

    pushAnswer('ans-2')
    await streamAnswer(
      runId,
      'ans-2',
      '上下文已对齐。接下来并行检索夜景构图与逆光霓虹样本。',
      timeline,
      'running',
      headerMeta,
    )
    await sleep(320)

    // —— 阶段 3：并行图库（含失败重试）——
    pushProcess({
      id: 'proc-gallery',
      type: 'process',
      summary: '并行检索图库 · 2 路（含 1 次重试）',
      status: 'running',
      collapsedByDefault: true,
      lines: [
        {
          id: 'g1',
          kind: 'tool',
          label: `${agentToolLabel('reference.gallery.list')} · 夜景街道`,
          status: 'running',
          meta: '并行',
        },
        {
          id: 'g2',
          kind: 'tool',
          label: `${agentToolLabel('reference.gallery.list')} · 逆光霓虹`,
          status: 'running',
          meta: '并行',
        },
      ],
    })
    publish('running')
    await sleep(820)

    const procGallery = timeline.find((b) => b.id === 'proc-gallery' && b.type === 'process')
    if (procGallery && procGallery.type === 'process') {
      procGallery.lines = [
        {
          id: 'g1',
          kind: 'tool',
          label: `${agentToolLabel('reference.gallery.list')} · 夜景街道`,
          status: 'completed',
          meta: '8→3',
          detail: '巷口透视可用，边缘光偏暖',
        },
        {
          id: 'g2',
          kind: 'tool',
          label: `${agentToolLabel('reference.gallery.list')} · 逆光霓虹`,
          status: 'failed',
          meta: '超时',
        },
        {
          id: 'g2r',
          kind: 'tool',
          label: `重试 · 逆光霓虹`,
          status: 'completed',
          meta: '5→2',
          detail: '低视平线 + 冷色边缘光',
        },
        {
          id: 'o-g',
          kind: 'observation',
          label: '收敛：构图 B + 逆光 D + 霓虹 E',
          status: 'completed',
        },
      ]
      procGallery.status = 'completed'
      procGallery.summary = '图库检索完成 · 2 路并行（含 1 次重试）'
    }
    setTodoStatus('t3', 'completed')
    setTodoStatus('t4', 'completed')
    setTodoStatus('t5', 'in_progress')
    publish('running')
    await sleep(300)

    pushAnswer('ans-3')
    await streamAnswer(
      runId,
      'ans-3',
      '候选已收敛。建议压低视平线、加强逆光与冷色霓虹，并保持第 2→4 页镜头推进。',
      timeline,
      'running',
      headerMeta,
    )
    await sleep(360)

    setTodoStatus('t5', 'completed')
    setTodoStatus('t6', 'completed')
    setTodoStatus('t7', 'in_progress')

    // —— 阶段 4：结论 + 确认（处理组展开承载确认）——
    pushProcess({
      id: 'proc-confirm',
      type: 'process',
      summary: `等待确认「${agentToolLabel('comic.page.generate')}」`,
      status: 'awaiting_confirmation',
      collapsedByDefault: false,
      forceOpen: true,
      lines: [
        {
          id: 'th-final',
          kind: 'thought',
          label: '提示词：低视平线、强逆光剪影、冷色霓虹边缘光',
          status: 'completed',
        },
        {
          id: 'o-final',
          kind: 'observation',
          label: '写入动作需确认后才会真正创建任务',
          status: 'awaiting_confirmation',
        },
      ],
    })

    pushAnswer('ans-final')
    await streamAnswer(
      runId,
      'ans-final',
      `已完成多轮检索与对比（含并行工具与 1 次失败重试）。创建「${agentToolLabel('comic.page.generate')}」前需要你确认。`,
      timeline,
      'awaiting_confirmation',
      headerMeta,
      true,
    )

    setPendingAction({
      token: `preview-${Date.now()}`,
      toolName: 'comic.page.generate',
      input: {
        projectId: 'P123',
        episodeId: 'E1',
        pageId: 'PG3',
        editPrompt: '更有压迫感的夜景街道重绘，逆光与冷色霓虹',
        references: ['gallery:B', 'gallery:D', 'gallery:E'],
      },
      assistantThought: '多轮检索与对比已完成；确认后才会真正创建页级生图任务。',
    })
  }

  function scrollViewportToBottom(force = false) {
    // 非强制（流式事件驱动）时仅在用户处于底部附近才跟随；用户主动发送等 force 调用无视
    if (!force && !stickToBottomRef.current) return
    requestAnimationFrame(() => {
      const node = messageViewportRef.current
      if (node) {
        stickToBottomRef.current = true
        node.scrollTo({ top: node.scrollHeight, behavior: 'smooth' })
      }
    })
  }

  async function handleRun() {
    const text = goal.trim()
    if (!canRun) {
      showToast({ message: '暂无智能体运行权限', variant: 'warning' })
      return
    }
    if (!agentReady) {
      showToast({ message: agentUnavailableReason || '智能体暂不可用', variant: 'warning' })
      return
    }
    if (!text) {
      showToast({ message: '请先输入目标', variant: 'warning' })
      return
    }
    const submittedModel = resolveAgentModelValue(model, clientUi.textModels)
    if (!submittedModel) {
      showToast({ message: '所选模型不可用', variant: 'warning' })
      return
    }
    setRunning(true)
    setComposerCollapsed(true)
    const submittedGoal = text
    const submittedMode = mode
    const modelLabel =
      modelOptions.find((item) => item.value === model.trim())?.label ?? submittedModel
    const modeLabel = MODE_OPTIONS.find((item) => item.value === submittedMode)?.label ?? submittedMode

    pushMessage({
      id: `user-${Date.now()}`,
      role: 'user',
      text: submittedGoal,
    })
    setGoal('')
    scrollViewportToBottom(true)

    const runId = `assistant-run-${Date.now()}`
    setActiveRunId(runId)
    answerScrollKeyRef.current = ''
    latestTimelineRef.current = []
    pushMessage({
      id: runId,
      role: 'assistant',
      status: 'running',
      meta: `${modelLabel} · ${modeLabel}`,
      timeline: [],
      activity: {
        label:
          submittedMode === 'auto'
            ? '正在判断执行模式…'
            : submittedMode === 'plan_execute'
              ? '正在执行计划模式…'
              : '正在执行即时模式…',
        kind: submittedMode === 'plan_execute' ? 'planning' : 'thinking',
      },
    })

    if (PREVIEW_SIMULATE_LOOP) {
      try {
        await playSimulatedLoop(runId, submittedGoal, submittedMode, modelLabel, modeLabel)
      } finally {
        setRunning(false)
      }
      return
    }

    streamAbortRef.current?.()
    setActiveRunPublicId(null)
    setPendingAction(null)

    let finalSelectedMode: string | undefined

    const handle = streamAgentRun(
      {
        goal: submittedGoal,
        mode: submittedMode,
        model: submittedModel,
        // 禁用清单随请求下发：模型看不到被禁用的工具定义，从源头不可调用
        ...(disabledToolNames.length > 0 ? { disabledTools: disabledToolNames } : {}),
        // 斜杠引用的技能随请求下发，正文由后端注入执行器上下文
        ...(selectedSkillIds.length > 0 ? { skillIds: selectedSkillIds } : {}),
      },
      {
        onEvent: (eventName, payload) => {
          bumpStreamActivity()
          const applied = applyAgentStreamEvent(latestTimelineRef.current, eventName, payload)
          latestTimelineRef.current = applied.timeline
          if (applied.runPublicId) setActiveRunPublicId(applied.runPublicId)
          if (eventName === 'confirmation_required' && applied.pendingAction) {
            setPendingAction(applied.pendingAction)
          }
          if (eventName === 'final' && isRecord(payload)) {
            const response = payload as Partial<AgentRunResponse>
            if (response.selectedMode) finalSelectedMode = response.selectedMode
          }
          patchMessage(runId, {
            timeline: applied.timeline,
            // 事件未携带 status 时不覆盖（spread undefined 会把运行态抹掉，导致打字机失效）
            ...(applied.status !== undefined ? { status: applied.status } : {}),
            showConfirm: applied.showConfirm,
            activity: applied.activity,
          })
          scrollViewportToBottom()
        },
        onError: (error) => {
          clearStreamIdle()
          showApiErrorToast(showToast, error, '运行智能体失败', 'error')
          const applied = applyAgentStreamEvent(latestTimelineRef.current, 'error', {
            status: 'failed',
            errorMessage: error instanceof Error ? error.message : '运行智能体失败',
          })
          latestTimelineRef.current = applied.timeline
          if (applied.pendingAction) setPendingAction(applied.pendingAction)
          patchMessage(runId, {
            status: 'failed',
            showConfirm: false,
            timeline: applied.timeline,
            activity: applied.activity ?? { label: '发送失败', kind: 'error' },
          })
        },
        onDone: () => {
          clearStreamIdle()
          setRunning(false)
          if (finalSelectedMode && finalSelectedMode !== submittedMode && finalSelectedMode !== 'auto') {
            patchMessage(runId, {
              meta: `${modelLabel} · ${
                MODE_OPTIONS.find((item) => item.value === finalSelectedMode)?.label || finalSelectedMode
              }`,
            })
          }
        },
      },
    )
    streamAbortRef.current = () => handle.abort()
  }

  async function handleConfirm(approved: boolean, answers?: string[]) {
    if (!pendingAction) return
    const action = pendingAction
    const isPreview = PREVIEW_SIMULATE_LOOP && action.token.startsWith('preview-')
    // 预览模拟不走真实权限；正式确认仍校验
    if (!isPreview && !canConfirm) {
      showToast({ message: '暂无确认执行权限', variant: 'warning' })
      return
    }
    setConfirming(true)
    const runId = activeRunId
    try {
      if (isPreview) {
        setPendingAction(null)
        if (runId) {
          const kept: AgentTimelineBlock[] = cloneTimeline(latestTimelineRef.current)
            .filter((block) => block.id !== 'ans-final')
            .map((block) => {
              if (block.type === 'todos') {
                return {
                  ...block,
                  forceOpen: false,
                  collapsedByDefault: true,
                  items: block.items.map((item) =>
                    item.id === 't7'
                      ? {
                          ...item,
                          status: approved ? ('completed' as const) : ('cancelled' as const),
                        }
                      : item.status === 'in_progress'
                        ? {
                            ...item,
                            status: approved ? ('completed' as const) : ('cancelled' as const),
                          }
                        : item,
                  ),
                }
              }
              if (block.type !== 'process') return block
              if (block.id === 'proc-confirm') {
                return {
                  ...block,
                  status: approved ? 'completed' : 'cancelled',
                  summary: approved
                    ? `已确认「${agentToolLabel(action.toolName)}」`
                    : `已取消「${agentToolLabel(action.toolName)}」`,
                  forceOpen: false,
                  collapsedByDefault: true,
                  lines: block.lines.map((line) =>
                    line.id === 'o-final'
                      ? {
                          ...line,
                          status: approved ? 'completed' : 'cancelled',
                          label: approved ? '已创建页级生图任务（预览）' : '用户已取消',
                        }
                      : line,
                  ),
                }
              }
              return { ...block, collapsedByDefault: true, forceOpen: false }
            })

          if (approved) {
            kept.push({
              id: 'proc-generate',
              type: 'process',
              summary: `已调用「${agentToolLabel(action.toolName)}」`,
              status: 'completed',
              collapsedByDefault: true,
              forceOpen: false,
              lines: [
                {
                  id: 't-gen',
                  kind: 'tool',
                  label: agentToolLabel(action.toolName),
                  status: 'completed',
                  detail: '任务进入排队',
                },
                {
                  id: 'o-gen',
                  kind: 'observation',
                  label: 'job_preview_001 · queued',
                  status: 'completed',
                },
              ],
            })
          }

          const finalText = approved
            ? `已确认「${agentToolLabel(action.toolName)}」。模拟续跑：已创建页级生图任务，并完成本次运行。`
            : `已取消「${agentToolLabel(action.toolName)}」，本次运行在确认点结束。`
          kept.push({ id: 'ans-done', type: 'answer', text: '' })
          latestTimelineRef.current = kept
          await streamAnswer(
            runId,
            'ans-done',
            finalText,
            kept,
            approved ? 'succeeded' : 'cancelled',
            messages.find((m) => m.id === runId)?.meta ?? '',
            false,
          )
        }
        showToast({
          message: approved ? '预览：已确认并续跑完成' : '预览：已取消待确认动作',
          variant: approved ? 'success' : 'warning',
          autoCloseMs: 2600,
        })
        setConfirming(false)
        return
      }

      if (runId) {
        patchMessage(runId, {
          status: 'running',
          showConfirm: false,
          activity: {
            label: approved
              ? (askUserQuestions ? '正在提交回答…' : '正在执行已确认动作…')
              : (askUserQuestions ? '已跳过回答，正在继续…' : '正在取消待确认动作…'),
            kind: approved ? 'tool' : 'done',
          },
        })
      }
      setPendingAction(null)

      const handle = streamAgentConfirm(
        {
          token: action.token,
          approved,
          ...(approved && answers && answers.length > 0 ? { answers } : {}),
          runPublicId: action.runPublicId ?? activeRunPublicId,
        },
        {
          onEvent: (eventName, payload) => {
            bumpStreamActivity()
            const applied = applyAgentStreamEvent(latestTimelineRef.current, eventName, payload)
            latestTimelineRef.current = applied.timeline
            if (applied.runPublicId) setActiveRunPublicId(applied.runPublicId)
            if (eventName === 'confirmation_required' && applied.pendingAction) {
              setPendingAction(applied.pendingAction)
            }
            if (runId) patchMessage(runId, {
              timeline: applied.timeline,
              status: applied.status as string,
              showConfirm: applied.showConfirm,
              activity: applied.activity ?? undefined,
            })
            scrollViewportToBottom()
          },
          onError: (error) => {
            clearStreamIdle()
            showApiErrorToast(showToast, error, approved ? '确认执行失败' : '取消待执行动作失败', 'error')
            if (runId) {
              const applied = applyAgentStreamEvent(latestTimelineRef.current, 'error', {
                status: 'failed',
                errorMessage: error instanceof Error ? error.message : (approved ? '确认执行失败' : '取消待执行动作失败'),
              })
              latestTimelineRef.current = applied.timeline
              patchMessage(runId, {
                status: 'failed',
                showConfirm: false,
                timeline: applied.timeline,
                activity: applied.activity ?? { label: '运行失败', kind: 'error' },
              })
            }
          },
          onDone: () => {
            clearStreamIdle()
            setConfirming(false)
          },
        },
      )
      streamAbortRef.current = () => handle.abort()
    } catch (error) {
      showApiErrorToast(showToast, error, approved ? '确认执行失败' : '取消待执行动作失败', 'error')
      setConfirming(false)
    }
  }

  return (
    <ContentSkeleton
      title="智能体"
      breadcrumbs={[{ label: '首页', to: '/' }, { label: '智能体' }]}
      adminLayout
      framed={false}
      scrollableBody={false}
      shellScrollable={false}
      headerTrailing={
        <ChromeToolbarButton
          type="button"
          className="h-9 lg:hidden"
          onClick={() => void refreshTools()}
          disabled={toolsLoading}
        >
          {toolsLoading ? '刷新中…' : '刷新工具'}
        </ChromeToolbarButton>
      }
    >
      <div className="flex min-h-0 min-w-0 flex-1 overflow-hidden lg:h-full lg:min-h-0 lg:flex-row">
        <section className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-[radial-gradient(circle_at_top_left,color-mix(in_srgb,var(--accent)_16%,transparent),transparent_38%),linear-gradient(180deg,color-mix(in_srgb,var(--shell-content-bg)_94%,transparent),var(--shell-content-bg))]">
          <div
            ref={messageViewportRef}
            className="scroll-region min-h-0 flex-1 overflow-y-auto px-3 py-3 sm:px-5 sm:py-4 lg:px-6"
            onScroll={handleViewportScroll}
          >
            <div className="mx-auto flex min-h-full w-full max-w-3xl flex-col gap-3 pb-4">
              {messages.map((message) => (
                <AgentRunCard
                  key={message.id}
                  role={message.role}
                  text={message.text}
                  status={message.status}
                  meta={message.meta}
                  timeline={message.timeline}
                  activity={message.activity}
                  thinking={streamIdle && message.id === activeRunId && message.status === 'running'}
                  openBlockIds={openBlockIds}
                  onBlockToggle={handleBlockToggle}
                  onGenerationCardChange={
                    message.id === activeRunId || message.role === 'assistant'
                      ? (blockId, patch) => patchGenerationCard(message.id, blockId, patch)
                      : undefined
                  }
                  confirmInline={
                    message.showConfirm && pendingAction && pendingAction.toolName !== 'ask_user' && message.id === activeRunId
                      ? {
                          toolName: pendingAction.toolName,
                          summary: humanizePendingSummary(pendingAction),
                          confirming,
                          onConfirm: () => void handleConfirm(true),
                          onCancel: () => void handleConfirm(false),
                        }
                      : null
                  }
                />
              ))}
            </div>
          </div>

          <AgentComposerDock
            angular={angular}
            goal={goal}
            mode={mode}
            model={model}
            running={running}
            canRun={canSubmit}
            unavailableHint={agentReady ? undefined : agentUnavailableReason}
            collapsed={composerCollapsed}
            modeOptions={modeOptions}
            modelOptions={modelOptions}
            onGoalChange={setGoal}
            onModeChange={(value) => setMode(value as ExplicitAgentMode)}
            onModelChange={setModel}
            onCollapsedChange={setComposerCollapsed}
            onSubmit={() => void handleRun()}
            toolsPicker={
              toolsAvailable && tools.length > 0 ? (
                <AgentInlinePicker
                  value=""
                  options={toolOptions}
                  onChange={() => {}}
                  onToggle={toggleTool}
                  selectedValues={disabledToolNames}
                  multiSelect
                  triggerLabel={toolsTriggerLabel}
                  disabled={running || !canRun}
                  ariaLabel="工具开关"
                  menuTitle="本次运行启用的工具（勾掉即禁用）"
                  minMenuWidth={300}
                />
              ) : null
            }
            skillOptions={canRun ? skillOptions : []}
            selectedSkills={selectedSkills}
            onSkillToggle={toggleSkill}
            onSkillRemove={toggleSkill}
            headerLeading={
              <ChromeMetaTag
                angular={angular}
                className={
                  toolsLoading
                    ? 'agent-composer-dock__status-tag agent-composer-dock__status-tag--pending'
                    : toolsAvailable
                      ? 'agent-composer-dock__status-tag agent-composer-dock__status-tag--ready'
                      : 'agent-composer-dock__status-tag agent-composer-dock__status-tag--unavailable'
                }
              >
                {toolCountLabel}
              </ChromeMetaTag>
            }
          />

          {askUserQuestions ? (
            <AgentQuestionOverlay
              key={pendingAction?.token ?? 'ask'}
              angular={angular}
              questions={askUserQuestions}
              onSubmit={(answers) => void handleConfirm(true, answers)}
              onClose={() => void handleConfirm(false)}
            />
          ) : null}
        </section>

        <aside
          className={[
            'shell-rail hidden h-full min-h-0 w-16 shrink-0 flex-col items-center border-line bg-panel/70 lg:flex',
            angular ? 'border-l-2' : 'border-l',
          ].join(' ')}
        >
          <div className="scroll-region flex min-h-0 flex-1 flex-col items-center gap-3 overflow-y-auto p-2">
            <ChromeTooltip content="刷新工具" angular={angular} side="top" align="center">
              <span className="inline-flex">
                <ChromeToolbarButton
                  type="button"
                  className="h-10 w-10 !p-0"
                  onClick={() => void refreshTools()}
                  disabled={toolsLoading}
                  aria-label="刷新工具"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-5 w-5"
                  >
                    <path d="M21 12a9 9 0 1 1-2.64-6.36" />
                    <path d="M21 3v6h-6" />
                  </svg>
                </ChromeToolbarButton>
              </span>
            </ChromeTooltip>
            <ChromeTooltip content="清空对话" angular={angular} side="top" align="center">
              <span className="inline-flex">
                <ChromeToolbarButton
                  type="button"
                  className="h-10 w-10 !p-0"
                  onClick={() => setConfirmClearOpen(true)}
                  disabled={messages.length === 0 || running}
                  aria-label="清空对话"
                >
                  <IconTrash className="h-5 w-5" aria-hidden />
                </ChromeToolbarButton>
              </span>
            </ChromeTooltip>
          </div>
        </aside>
      </div>
      <ThemeModal
        open={confirmClearOpen}
        onClose={() => setConfirmClearOpen(false)}
        title="清空对话？"
        angular={angular}
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <ChromeToolbarButton
              type="button"
              onClick={() => setConfirmClearOpen(false)}
            >
              取消
            </ChromeToolbarButton>
            <ChromeToolbarDangerButton
              type="button"
              onClick={() => {
                setConfirmClearOpen(false)
                clearConversation()
              }}
            >
              确认清空
            </ChromeToolbarDangerButton>
          </div>
        }
      >
        <p className="text-sm leading-relaxed text-ink">
          清空后当前对话与进行中的任务展示不可恢复，正在生成的结果可在生图页时间线查看。
        </p>
      </ThemeModal>
    </ContentSkeleton>
  )
}
